// Please remove eslint-disable and all consoles and exlint issues before pushing the code

import {
  LocalAudioTrack,
  LocalParticipant,
  LocalTrackPublication,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
  VideoPresets,
} from "livekit-client";
import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { clearReduxPersist } from "./persistStore";
import { updateRoomleft } from "./meeting";
import natsService from "src/services/NatsService";
import { DtlnNoiseSuppressionProcessor } from "src/utils/DtlnProcessor";
import { getParticipantData } from "./events/Resource_Assignment";
import { playSound } from "src/utils/soundManager";
{
  /* eslint-disable no-console */
}

let processor: DtlnNoiseSuppressionProcessor | null = null;

/**
 * Determines the priority of an audio input device based on its label.
 */
const getDevicePriority = (device: MediaDeviceInfo): number => {
  const label = device.label.toLowerCase();
  if (
    label.includes("bluetooth") ||
    label.includes("wireless") ||
    label.includes("headset") ||
    label.includes("headphone") ||
    label.includes("neckband") ||
    label.includes("buds")
  ) {
    return 3;
  }
  if (label.includes("usb") || label.includes("external")) {
    return 2;
  }
  return 1;
};

/**
 * Selects the best available audio input device based on priority.
 */
const selectBestAudioDevice = (
  devices: MediaDeviceInfo[],
): MediaDeviceInfo | null => {
  const audioInputs = devices.filter(
    (device) => device.kind === "audioinput" && device.deviceId !== "",
  );

  if (audioInputs.length === 0) {
    return null;
  }

  const sortedDevices = audioInputs.sort(
    (a, b) => getDevicePriority(b) - getDevicePriority(a),
  );

  return sortedDevices[0];
};

const initializeProcessor = async (originalAudioTrack: MediaStreamTrack) => {
  if (processor) {
    try {
      processor.stopProcessing();
    } catch (err) {
      console.warn("Failed to stop previous processor:", err);
    }
  }

  if (!processor) {
    try {
      processor = new DtlnNoiseSuppressionProcessor();
    } catch (err) {
      console.error("Failed to create processor:", err);
      return null;
    }
  }

  try {
    const gainValueEnv = process.env.REACT_APP_NOISE_SUPPRESSION_GAIN;
    const gainValue =
      gainValueEnv && !isNaN(parseFloat(gainValueEnv))
        ? parseFloat(gainValueEnv)
        : 1.8;
    const processedAudioTrack =
      await processor.startProcessing(originalAudioTrack, gainValue);
    return processedAudioTrack;
  } catch (err) {
    console.error("Failed to process track:", err);
    return null;
  }
};

const LIVEKIT_SERVER_URL =
  process.env.LIVEKIT_SERVER_URL || "wss://live-01.einston.io";
const APP_BASE_URL = process.env.REACT_APP_API_URL;

interface IRootState {
  room: Room;
  participants: RemoteParticipant[];
  localParticipant: LocalParticipant;
  isMicrophoneEnabled: boolean;
  screenSharingTrack: RemoteTrack;
  screenSharingParticipantName: string;
  isVideoEnabled: boolean;
  someChanges: boolean;
  isNoiseSuppressionEnabled: boolean;
  currentMicrophoneDeviceId: string;
  isManualDeviceSelection: boolean;
}

const initialState: IRootState = {
  room: null,
  participants: [],
  localParticipant: null,
  screenSharingTrack: null,
  screenSharingParticipantName: "",
  isMicrophoneEnabled: false,
  isVideoEnabled: false,
  someChanges: false,
  isNoiseSuppressionEnabled: true,
  currentMicrophoneDeviceId: "default",
  isManualDeviceSelection: false,
};

const slice = createSlice({
  name: "livekitMeeting",
  initialState,
  reducers: {
    setRoom(state, action) {
      state.room = action.payload;
    },
    setLocalParticipant(state, action) {
      state.localParticipant = action.payload;
    },
    setParticipants(state, action) {
      state.participants = action.payload;
    },
    setMicrophoneEnabled(state, action) {
      state.isMicrophoneEnabled = action.payload;
    },
    setVideoEnabled(state, action) {
      state.isVideoEnabled = action.payload;
    },
    setSomeChanges(state) {
      state.someChanges = !state.someChanges;
    },
    setScreenSharingTrack(state, action) {
      state.screenSharingTrack = action.payload;
    },
    setScreenSharingParticipantname(state, action) {
      state.screenSharingParticipantName = action.payload;
    },
    setNoiseSuppressionEnabled(state, action) {
      state.isNoiseSuppressionEnabled = action.payload;
    },
    setCurrentMicrophoneDeviceId(state, action) {
      state.currentMicrophoneDeviceId = action.payload;
    },
    setManualDeviceSelection(state, action) {
      state.isManualDeviceSelection = action.payload;
    },
  },
});

export const createRoom = () => async (dispatch, getState) => {
  const res = await axios.get(`${APP_BASE_URL}/aacl/api/v1/aacl/getToken`);
  const { data } = res.data;
  const room = new Room({
    adaptiveStream: true,
    dynacast: true,
    videoCaptureDefaults: {
      resolution: VideoPresets.h720.resolution,
    },
  });
  // room.prepareConnection(LIVEKIT_SERVER_URL, data);
  // Event listeners for any changes in the room (meeting)
  room
    .on(
      RoomEvent.TrackSubscribed,
      (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        dispatch(handleTrackSubscribed(track, publication, participant));
      },
    )
    .on(
      RoomEvent.TrackUnsubscribed,
      (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => dispatch(handleTrackUnsubscribed(track, publication, participant)),
    )
    .on(RoomEvent.Disconnected, handleDisconnect)
    .on(
      RoomEvent.LocalTrackUnpublished,
      (publication: LocalTrackPublication) => {
        handleLocalTrackUnpublished(publication);
        dispatch(slice.actions.setSomeChanges());
      },
    )
    .on(RoomEvent.LocalTrackPublished, () =>
      dispatch(slice.actions.setSomeChanges()),
    )
    .on(RoomEvent.TrackUnmuted, () => dispatch(slice.actions.setSomeChanges()))
    .on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
      dispatch(handleParticipantConnected(participant));
    })
    .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      dispatch(handleParticipantDisconnected(participant));
    })
    .on(RoomEvent.TrackMuted, () => dispatch(slice.actions.setSomeChanges()));

  await room.connect(LIVEKIT_SERVER_URL, data);
  const { soundNotificationonJoinOrLeave } = getState().meeting;
  if (soundNotificationonJoinOrLeave) {
    playSound("/sounds/sound-effect-551 1.mp3");
  }
  const remoteParticipants = Array.from(room.remoteParticipants.values());
  dispatch(slice.actions.setParticipants(remoteParticipants));
  const localParticipant = room.localParticipant;
  await localParticipant.setCameraEnabled(false);

  dispatch(slice.actions.setLocalParticipant(localParticipant));
  dispatch(slice.actions.setRoom(room));

  const devices = await Room.getLocalDevices("audioinput");
  const preferredDevice = selectBestAudioDevice(devices);
  const deviceId = preferredDevice?.deviceId || "default";

  dispatch(slice.actions.setCurrentMicrophoneDeviceId(deviceId));

  const { defaultAudioStatus } = getState().AVSetting;
  dispatch(slice.actions.setMicrophoneEnabled(!!defaultAudioStatus));

  const isNoiseSuppressionEnabled =
    getState().livekitMeeting.isNoiseSuppressionEnabled;

  await dispatch(republishMicrophoneTrack(deviceId, isNoiseSuppressionEnabled));
};

export const toggleNoiseSuppression = () => async (dispatch, getState) => {
  const state = getState();
  const localParticipant = state.livekitMeeting.localParticipant;

  if (!localParticipant) return;

  const currentStatus = state.livekitMeeting.isNoiseSuppressionEnabled;
  const currentDeviceId = state.livekitMeeting.currentMicrophoneDeviceId;
  const newStatus = !currentStatus;

  dispatch(slice.actions.setNoiseSuppressionEnabled(newStatus));
  dispatch(republishMicrophoneTrack(currentDeviceId, newStatus));
};

export const republishMicrophoneTrack =
  (
    deviceId: string,
    shouldApplyNoiseSuppression: boolean,
    preserveMuteState: boolean = true,
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const localParticipant = state.livekitMeeting.localParticipant;

    if (!localParticipant) return;

    const isReduxMicrophoneEnabled = state.livekitMeeting.isMicrophoneEnabled;
    const wasMuted = !isReduxMicrophoneEnabled;

    try {
      const existingPubs = Array.from(
        localParticipant.trackPublications.values(),
      ).filter(
        (pub: any) => pub.source === Track.Source.Microphone,
      ) as LocalTrackPublication[];

      for (const pub of existingPubs) {
        if (pub.track) {
          await localParticipant.unpublishTrack(pub.track);
        }
      }

      if (processor) {
        try {
          processor.stopProcessing();
        } catch (err) {
          throw new Error("Failed to stop previous audio processor:", err);
        }
        processor = null;
      }

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          noiseSuppression: !shouldApplyNoiseSuppression,
          echoCancellation: true,
          autoGainControl: true,
        },
      });
      const micTrack = micStream.getAudioTracks()[0];

      let trackToPublish;

      if (shouldApplyNoiseSuppression) {
        const processedTrack = await initializeProcessor(micTrack);
        if (!processedTrack) {
          console.error("Failed to process track with noise suppression");
          return;
        }
        trackToPublish = new LocalAudioTrack(processedTrack);
      } else {
        trackToPublish = new LocalAudioTrack(micTrack);
      }

      const publishOptions = {
        source: Track.Source.Microphone,
        red: true,
        dtx: true,
      };

      if (preserveMuteState) {
        if (wasMuted) {
          await trackToPublish.mute();
          await localParticipant.publishTrack(trackToPublish, publishOptions);

          await localParticipant.setMicrophoneEnabled(false);
          dispatch(slice.actions.setMicrophoneEnabled(false));
        } else {
          await localParticipant.publishTrack(trackToPublish, publishOptions);
          await localParticipant.setMicrophoneEnabled(true);
          dispatch(slice.actions.setMicrophoneEnabled(true));
        }
      } else {
        await localParticipant.publishTrack(trackToPublish, publishOptions);
        await localParticipant.setMicrophoneEnabled(false);
        dispatch(slice.actions.setMicrophoneEnabled(false));
      }

      dispatch(slice.actions.setCurrentMicrophoneDeviceId(deviceId));
    } catch (error) {
      console.error("Error republishing microphone track:", error);
    }
  };

const handleParticipantConnected =
  (participant: RemoteParticipant) => (dispatch, getState) => {
    const participants = getState()?.livekitMeeting?.participants?.filter(
      (p) => p.sid !== participant.sid,
    );
    dispatch(slice.actions.setParticipants([...participants, participant]));
    dispatch(getParticipantData());
  };

const handleParticipantDisconnected =
  (participant: RemoteParticipant) => (dispatch, getState) => {
    const participants = getState()?.livekitMeeting?.participants?.filter(
      (p) => p.sid !== participant.sid,
    );
    dispatch(slice.actions.setParticipants([...participants]));
  };

const handleTrackSubscribed =
  (
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ) =>
  (dispatch, getState) => {
    if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
      if (track.kind === Track.Kind.Video && track.source === "screen_share") {
        dispatch(slice.actions.setScreenSharingTrack(track));
        dispatch(
          slice.actions.setScreenSharingParticipantname(participant.name),
        );
        //this is to check if participant already has screen shared then need to stop the screen sharing
        const localParticipant = getState().livekitMeeting.localParticipant;
        if (localParticipant?.isScreenShareEnabled) {
          localParticipant?.setScreenShareEnabled(false);
        }
      }
      dispatch(slice.actions.setSomeChanges());
    }
  };

export const startScreenShareLivekit = () => async (dispatch, getState) => {
  const localParticipant = getState()?.livekitMeeting?.room?.localParticipant;
  await localParticipant.setScreenShareEnabled(true);
};

export const stopScreenShareLivekit =
  () => async (dispatch, getState) => {
    const localParticipant = getState()?.livekitMeeting?.room?.localParticipant;
    await localParticipant.setScreenShareEnabled(false);
  };

export const handleMicToggle =
  (enqueueSnackbar) => async (dispatch, getState) => {
    const room = getState()?.livekitMeeting?.room;
    const localParticipant = room.localParticipant;
    try {
      if (localParticipant.isMicrophoneEnabled) {
        await localParticipant.setMicrophoneEnabled(false);
        dispatch(
          slice.actions.setMicrophoneEnabled(
            localParticipant.isMicrophoneEnabled,
          ),
        );
      } else {
        await localParticipant.setMicrophoneEnabled(true);
        dispatch(
          slice.actions.setMicrophoneEnabled(
            localParticipant.isMicrophoneEnabled,
          ),
        );
      }
    } catch (error) {
      console.log("Error toggling microphone:", error);
      enqueueSnackbar("Please enable microphone permission", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    }
  };

export const leaveRoom = () => async (dispatch) => {
  dispatch(clearReduxPersist());
  natsService.closeConnection();
  dispatch(updateRoomleft(true));
};

export const handleVideoToggle =
  (enqueueSnackbar) => async (dispatch, getState) => {
    const room = getState()?.livekitMeeting?.room;
    const localParticipant = room.localParticipant;
    try {
      if (localParticipant.isCameraEnabled) {
        await localParticipant.setCameraEnabled(false);
      } else {
        await localParticipant.setCameraEnabled(true);
      }
      dispatch(slice.actions.setVideoEnabled(localParticipant.isCameraEnabled));
    } catch (error) {
      console.log("Error toggling video:", error);
      enqueueSnackbar("Please enable video permission", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    }
  };

export const getMicrophoneDevices = () => async () => {
  const devices = await Room.getLocalDevices("audioinput");
  console.log("devices", devices);
};

const handleTrackUnsubscribed =
  (
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ) =>
  (dispatch, getState) => {
    if (track.kind === Track.Kind.Video && track.source === "screen_share") {
      const currentScreenTrackId =
        getState().livekitMeeting.screenSharingTrack?.sid;
      if (currentScreenTrackId === track.sid) {
        dispatch(slice.actions.setScreenSharingTrack(null));
        dispatch(slice.actions.setScreenSharingParticipantname(""));
      }
    }
    // remove tracks from all attached elements
    console.log("unsubscribed track", participant, track);
    track.detach();
  };

function handleLocalTrackUnpublished(
  publication: LocalTrackPublication,
  // participant: LocalParticipant
) {
  // when local tracks are ended, update UI to remove them from rendering
  console.log("unpublished track", publication);
  publication.track.detach();
}

function handleDisconnect() {
  console.log("disconnected from room");
}

export const sendChatMessage = (chatData) => async (dispatch, getState) => {
  const state = getState();
  const room = state.livekitMeeting?.room;
  try {
    if (room && room.localParticipant) {
      const messageText = chatData.content || chatData.message || "";
      await room.localParticipant.sendText(messageText, { topic: "lk.chat" });
    }
  } catch (error) {
    console.error("Error sending chat message:", error);
  }
};
export const reducer = slice.reducer;
export const livekitMeetingActions = slice.actions;
export default slice;
